import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { ResetPasswordInterface } from '@core/models/authentication.model';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import {
  SESSION_KEYS,
  FORM_FIELDS,
} from '@feature/auth/constants/auth.constant';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { REGEX_CONSTANT } from '@sharedModule/constants/pattern-regex.constant';
import { ROUTES } from '@sharedModule/constants/route.constants';
import {
  showValidationMsg,
  ConfirmPasswordValidator,
} from '@sharedModule/utils/form.utils';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputModule],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  public readonly attributeConstant = ATTRIBUTES_CONSTANT;
  public resetPasswordForm!: FormGroup;

  private readonly destroy$ = new Subject<void>();
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly messageHandlerService = inject(MessageHandlerService);

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSubmit(): void {
    if (!this.resetPasswordForm.valid) {
      showValidationMsg(this.resetPasswordForm);
      return;
    }

    const formData = this.resetPasswordForm.value as ResetPasswordInterface;
    this.performPasswordReset(formData);
  }

  private initializeComponent(): void {
    const userEmail = this.sessionStorageService.get(SESSION_KEYS.USER_EMAIL);

    if (!userEmail) {
      this.router.navigate([ROUTES.LOGIN]);
      return;
    }

    this.initializeForm(userEmail);
  }

  private initializeForm(userEmail: string): void {
    this.resetPasswordForm = this.formBuilder.group(
      {
        [this.attributeConstant.email.key]: [
          userEmail,
          [Validators.required, Validators.pattern(REGEX_CONSTANT.email)],
        ],
        [FORM_FIELDS.NEW_PASSWORD]: ['', [Validators.required]],
        [FORM_FIELDS.CONFIRM_PASSWORD]: ['', [Validators.required]],
      },
      {
        validators: ConfirmPasswordValidator(
          FORM_FIELDS.NEW_PASSWORD,
          FORM_FIELDS.CONFIRM_PASSWORD
        ),
      }
    );
  }

  private performPasswordReset(formData: ResetPasswordInterface): void {
    this.authenticationService
      .resetPassword(formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.success) {
            this.sessionStorageService.clear();
            this.messageHandlerService.show(response.message);
            this.router.navigate([ROUTES.LOGIN]);
          } else {
            this.messageHandlerService.show(response.message);
          }
        },
      });
  }
}
